import { useState, useEffect, useMemo } from "react";
import { useAuth, api } from "@/context/AuthContext";
import { compressImage } from "@/utils/imageCompression";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreVertical, Pencil, Trash2, Users, Phone, Mail, MapPin, Search, RefreshCw, Car, FileText, X, Eye, Camera, Loader2, Download } from "lucide-react";
import BusinessCardScanDialog from "@/components/BusinessCardScanDialog";
import VoiceInputButton from "@/components/VoiceInputButton";

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [docsCustomer, setDocsCustomer] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [cardScanOpen, setCardScanOpen] = useState(false);
  const [inlineScanning, setInlineScanning] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    street: "", postal_code: "", city: "", notes: "",
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [customersRes, vehiclesRes] = await Promise.all([
        api.get("/api/customers"),
        api.get("/api/vehicles"),
      ]);
      setCustomers(customersRes.data);
      setVehicles(vehiclesRes.data);
    } catch (err) {
      setError("Fehler beim Laden der Kunden");
      toast.error("Fehler beim Laden der Kunden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Map: customer_id -> list of vehicles
  const vehiclesByCustomer = useMemo(() => {
    const map = {};
    vehicles.forEach((v) => {
      if (!map[v.customer_id]) map[v.customer_id] = [];
      map[v.customer_id].push(v);
    });
    return map;
  }, [vehicles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await api.put(`/api/customers/${editingCustomer.id}`, formData);
        toast.success("Kunde aktualisiert");
      } else {
        await api.post("/api/customers", formData);
        toast.success("Kunde erstellt");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      first_name: customer.first_name || "",
      last_name: customer.last_name || "",
      phone: customer.phone || "",
      email: customer.email || "",
      street: customer.street || "",
      postal_code: customer.postal_code || "",
      city: customer.city || "",
      notes: customer.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm("Kunde und alle zugehörigen Fahrzeuge/Aufträge löschen?")) return;
    try {
      await api.delete(`/api/customers/${customerId}`);
      toast.success("Kunde gelöscht");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleOpenDocs = async (customer) => {
    setDocsCustomer(customer);
    setDocsDialogOpen(true);
    try {
      const res = await api.get(`/api/customers/${customer.id}/documents`);
      setDocuments(res.data);
    } catch {
      setDocuments([]);
    }
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !docsCustomer) return;
    // Hard limit: 25 MB after compression. Cloudinary free-tier raw limit = 100 MB,
    // but very large PDFs often time out on mobile networks - warn early.
    const isImage = file.type.startsWith("image/");
    if (!isImage && file.size > 25 * 1024 * 1024) {
      toast.error(
        `Datei ist ${(file.size / 1024 / 1024).toFixed(1)} MB. Bitte PDF auf max. 25 MB komprimieren (z.B. via PDF-Compress-Tool).`
      );
      e.target.value = "";
      return;
    }
    const uploading = toast.loading("Dokument wird hochgeladen...");
    try {
      // Only compress images. PDFs are passed through unchanged.
      const payload = isImage
        ? await compressImage(file, { maxWidth: 1920, quality: 0.85 })
        : file;
      const form = new FormData();
      form.append("file", payload);
      form.append("doc_type", "dokument");
      await api.post(`/api/customers/${docsCustomer.id}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success("Dokument hochgeladen", { id: uploading });
      const res = await api.get(`/api/customers/${docsCustomer.id}/documents`);
      setDocuments(res.data);
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          err.message ||
          "Fehler beim Hochladen",
        { id: uploading }
      );
    }
    e.target.value = "";
  };

  const handleDeleteDoc = async (docId) => {
    if (!docsCustomer) return;
    try {
      await api.delete(`/api/customers/${docsCustomer.id}/documents/${docId}`);
      toast.success("Dokument gelöscht");
      setDocuments(documents.filter((d) => d.id !== docId));
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  const resetForm = () => {
    setFormData({ first_name: "", last_name: "", phone: "", email: "", street: "", postal_code: "", city: "", notes: "" });
    setEditingCustomer(null);
  };

  const handleCardScanComplete = (action, ocrData) => {
    if (action === "fill_form" && ocrData) {
      // Fill form with scanned data for manual review
      setFormData({
        first_name: ocrData.first_name || "",
        last_name: ocrData.last_name || "",
        phone: ocrData.phone || "",
        email: ocrData.email || "",
        street: ocrData.street || "",
        postal_code: ocrData.postal_code || "",
        city: ocrData.city || "",
        notes: [ocrData.company, ocrData.position, ocrData.website].filter(Boolean).join(", "),
      });
      setEditingCustomer(null);
      setDialogOpen(true);
      toast.info("Daten ins Formular übernommen – bitte prüfen");
    } else {
      // Data was saved directly
      fetchData();
    }
  };

  const handleInlineScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setInlineScanning(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(file);
      });
      const res = await api.post("/api/customers/scan-business-card", {
        image_base64: base64,
        ocr_type: "business_card",
      });
      if (res.data.success) {
        const ocr = res.data.ocr_data;
        setFormData({
          first_name: ocr.first_name || "",
          last_name: ocr.last_name || "",
          phone: ocr.phone || "",
          email: ocr.email || "",
          street: ocr.street || "",
          postal_code: ocr.postal_code || "",
          city: ocr.city || "",
          notes: [ocr.company, ocr.position, ocr.website].filter(Boolean).join(", "),
        });
        toast.success("Visitenkarte erkannt – Felder ausgefüllt!");
      } else {
        toast.error(res.data.error || "Scannen fehlgeschlagen");
      }
    } catch (err) {
      toast.error("Fehler beim Scannen der Visitenkarte");
    } finally {
      setInlineScanning(false);
    }
  };

  const getFullAddress = (c) => {
    const parts = [];
    if (c.street) parts.push(c.street);
    if (c.postal_code || c.city) parts.push(`${c.postal_code || ""} ${c.city || ""}`.trim());
    return parts.join(", ");
  };

  // Filter by name, phone, email, or linked vehicle plate/VIN
  const filteredCustomers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = customers.filter((c) => {
      const name = c.name || `${c.first_name} ${c.last_name}`;
      if (name.toLowerCase().includes(term)) return true;
      if (c.phone && c.phone.includes(searchTerm)) return true;
      if (c.email && c.email.toLowerCase().includes(term)) return true;
      // Search by linked vehicle plate or VIN
      const cvehicles = vehiclesByCustomer[c.id] || [];
      return cvehicles.some(
        (v) =>
          v.license_plate.toLowerCase().includes(term) ||
          (v.vin && v.vin.toLowerCase().includes(term))
      );
    });
    // Sort alphabetically by last_name, then first_name
    return filtered.sort((a, b) => {
      const nameA = `${a.last_name || ""} ${a.first_name || ""}`.trim().toLowerCase();
      const nameB = `${b.last_name || ""} ${b.first_name || ""}`.trim().toLowerCase();
      return nameA.localeCompare(nameB, "de");
    });
  }, [customers, searchTerm, vehiclesByCustomer]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="mt-2 text-sm text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-muted-foreground">{error}</p>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Erneut versuchen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: "Barlow Condensed" }}>
            Kundenverwaltung
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">{customers.length} Kunden gesamt</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            className="gap-2 flex-1 sm:flex-initial"
            onClick={() => setCardScanOpen(true)}
            data-testid="scan-business-card-button"
          >
            <Camera className="w-4 h-4" /> Visitenkarte
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2 flex-1 sm:flex-initial" data-testid="add-customer-button">
              <Plus className="w-4 h-4" /> Neuer Kunde
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Barlow Condensed" }} className="text-xl">
                {editingCustomer ? "Kunde bearbeiten" : "Neuer Kunde"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Inline Visitenkarten-Scan im Formular */}
              {!editingCustomer && (
                <label className={`cursor-pointer block border-2 border-dashed rounded-lg p-3 text-center transition-colors ${inlineScanning ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}`}>
                  <div className="flex items-center justify-center gap-2">
                    {inlineScanning ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        <span className="text-sm font-medium text-primary">Visitenkarte wird analysiert...</span>
                      </>
                    ) : (
                      <>
                        <Camera className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Visitenkarte scannen & Felder ausfüllen</span>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handleInlineScan}
                    disabled={inlineScanning}
                    data-testid="inline-card-scan-input"
                  />
                </label>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Vorname *</Label>
                  <div className="flex gap-1.5">
                    <Input id="first_name" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="Max" required data-testid="customer-first-name-input" />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, first_name: t }))} data-testid="voice-first-name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nachname *</Label>
                  <div className="flex gap-1.5">
                    <Input id="last_name" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder="Mustermann" required data-testid="customer-last-name-input" />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, last_name: t }))} data-testid="voice-last-name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon *</Label>
                  <div className="flex gap-1.5">
                    <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="0151-12345678" required data-testid="customer-phone-input" />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, phone: t }))} data-testid="voice-phone" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <div className="flex gap-1.5">
                    <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="max@beispiel.de" data-testid="customer-email-input" />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, email: t.replace(/ /g, "").toLowerCase() }))} data-testid="voice-email" />
                  </div>
                </div>
                <div className="col-span-1 sm:col-span-2 space-y-2">
                  <Label htmlFor="street">Straße & Hausnummer</Label>
                  <div className="flex gap-1.5">
                    <Input id="street" value={formData.street} onChange={(e) => setFormData({ ...formData, street: e.target.value })} placeholder="Hauptstraße 15" data-testid="customer-street-input" />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, street: t }))} data-testid="voice-street" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">PLZ</Label>
                  <div className="flex gap-1.5">
                    <Input id="postal_code" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} placeholder="10115" data-testid="customer-postal-code-input" />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, postal_code: t.replace(/\s/g, "") }))} data-testid="voice-postal-code" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Ort</Label>
                  <div className="flex gap-1.5">
                    <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Berlin" data-testid="customer-city-input" />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, city: t }))} data-testid="voice-city" />
                  </div>
                </div>
                <div className="col-span-1 sm:col-span-2 space-y-2">
                  <Label htmlFor="notes">Notizen</Label>
                  <div className="flex gap-1.5">
                    <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} placeholder="Zusätzliche Informationen..." className="flex-1" data-testid="customer-notes-input" />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, notes: f.notes ? `${f.notes} ${t}` : t }))} data-testid="voice-notes" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                <Button type="submit" className="btn-primary" data-testid="customer-submit-button">
                  {editingCustomer ? "Speichern" : "Erstellen"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Suchen: Name, Telefon, E-Mail, Kennzeichen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="customer-search-input"
        />
      </div>

      {/* Table */}
      <Card className="border shadow-sm overflow-hidden" data-testid="customers-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="table-header">Name</TableHead>
                  <TableHead className="table-header hidden sm:table-cell">Telefon</TableHead>
                  <TableHead className="table-header hidden md:table-cell">E-Mail</TableHead>
                  <TableHead className="table-header hidden lg:table-cell">Fahrzeuge</TableHead>
                  <TableHead className="table-header hidden xl:table-cell">Adresse</TableHead>
                  <TableHead className="table-header w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer) => {
                    const cvehicles = vehiclesByCustomer[customer.id] || [];
                    return (
                      <TableRow key={customer.id} className="hover:bg-muted/30" data-testid={`customer-row-${customer.id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                              <Users className="w-4 h-4 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate font-semibold">{customer.name || `${customer.first_name} ${customer.last_name}`}</p>
                              <p className="text-xs text-muted-foreground sm:hidden">{customer.phone}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Phone className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{customer.phone}</span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {customer.email && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate max-w-[200px]">{customer.email}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {cvehicles.length > 0 ? (
                            <div className="space-y-1">
                              {cvehicles.map((v) => (
                                <Badge key={v.id} variant="outline" className="mr-1 text-xs font-normal">
                                  <Car className="w-3 h-3 mr-1" />
                                  {v.license_plate} - {v.brand} {v.model}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          {getFullAddress(customer) ? (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              <span className="truncate max-w-[200px]">{getFullAddress(customer)}</span>
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`customer-menu-${customer.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleOpenDocs(customer)} data-testid={`docs-customer-${customer.id}`}>
                                <FileText className="w-4 h-4 mr-2" /> Dokumente
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(customer)} data-testid={`edit-customer-${customer.id}`}>
                                <Pencil className="w-4 h-4 mr-2" /> Bearbeiten
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(customer.id)} className="text-destructive" data-testid={`delete-customer-${customer.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" /> Löschen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      {searchTerm ? "Keine Kunden gefunden" : "Noch keine Kunden vorhanden"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dokumente Dialog */}
      <Dialog open={docsDialogOpen} onOpenChange={setDocsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="documents-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Barlow Condensed" }} className="text-xl">
              Dokumente – {docsCustomer?.name || `${docsCustomer?.first_name} ${docsCustomer?.last_name}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Upload */}
            <label className="cursor-pointer block border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
              <div className="flex flex-col items-center gap-1">
                <Plus className="w-6 h-6 text-muted-foreground" />
                <p className="text-sm font-medium">Dokument hochladen</p>
                <p className="text-xs text-muted-foreground">Fahrzeugschein, Rechnung, etc.</p>
              </div>
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleUploadDoc} data-testid="upload-doc-input" />
            </label>

            {/* Documents list */}
            {documents.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {documents.map((doc) => {
                  const isPdf = (doc.filename || "").toLowerCase().endsWith(".pdf") ||
                                (doc.url || "").toLowerCase().includes(".pdf");
                  // Cloudinary fl_attachment forces download with the original filename,
                  // even for raw resources where Content-Type defaults to inline.
                  // URL pattern: .../upload/fl_attachment/v.../path → browser downloads.
                  const downloadUrl = doc.url && doc.url.includes("/upload/")
                    ? doc.url.replace("/upload/", "/upload/fl_attachment/")
                    : doc.url;
                  return (
                  <div key={doc.id} className="relative group rounded-lg border overflow-hidden" data-testid={`doc-${doc.id}`}>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Im Browser öffnen"
                    >
                      {isPdf ? (
                        <div className="w-full h-32 bg-red-50 flex flex-col items-center justify-center text-red-700 hover:bg-red-100 transition-colors">
                          <FileText className="w-10 h-10" />
                          <span className="text-[10px] font-bold mt-1 tracking-wider">PDF</span>
                        </div>
                      ) : (
                        <img src={doc.url} alt={doc.filename} className="w-full h-32 object-cover" />
                      )}
                    </a>
                    <a
                      href={doc.url}
                      download={doc.filename || (isPdf ? "dokument.pdf" : "dokument")}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title="Herunterladen"
                      className="absolute top-1 left-1 bg-slate-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-900"
                      data-testid={`download-doc-${doc.id}`}
                    >
                      <Download className="w-3 h-3" />
                    </a>
                    <div className="p-2">
                      <p className="text-xs font-medium truncate" title={doc.filename}>{doc.filename || doc.doc_type}</p>
                      <p className="text-xs text-muted-foreground">{new Date(doc.created_at).toLocaleDateString("de-DE")}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteDoc(doc.id)}
                      className="absolute top-1 right-1 bg-destructive text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-doc-${doc.id}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-sm text-muted-foreground py-6">Keine Dokumente vorhanden</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Visitenkarten-Scan Dialog */}
      <BusinessCardScanDialog
        open={cardScanOpen}
        onOpenChange={setCardScanOpen}
        customers={customers}
        onComplete={handleCardScanComplete}
      />
    </div>
  );
};

export default CustomersPage;
